import { useEffect, useState } from 'react';
import {
  DEFAULT_SIGNATURE_TEMPLATE,
  renderSignature,
  type SupportSettings,
} from '@supporchat/shared';
import { api } from '../lib/api';
import { renderWhatsappBold } from '../lib/whatsapp-text';

/**
 * Configuração da assinatura das mensagens escritas por gente.
 *
 * Fica junto das configurações do agente porque é a mesma pergunta vista do
 * outro lado — o que o cliente lê quando alguém responde —, mas tem estado e
 * salvamento próprios: mexer na assinatura não deve arrastar junto uma
 * alteração de prompt ainda não salva.
 */
export function SignatureCard({ isAdmin, userName }: { isAdmin: boolean; userName: string }) {
  const [settings, setSettings] = useState<SupportSettings | null>(null);
  const [draft, setDraft] = useState<SupportSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const current = await api.getSupportSettings();
        setSettings(current);
        setDraft(current);
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, []);

  if (!draft || !settings) {
    return (
      <div className="card">
        <h3 className="card__title">Assinatura dos atendentes</h3>
        {error ? <div className="alert alert--error">{error}</div> : <div className="empty">Carregando...</div>}
      </div>
    );
  }

  const update = <K extends keyof SupportSettings>(key: K, value: SupportSettings[K]) =>
    setDraft({ ...draft, [key]: value });

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const preview = renderSignature(draft.signatureTemplate || DEFAULT_SIGNATURE_TEMPLATE, {
    nome: userName,
    setor: draft.signatureDepartment,
  });

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api.updateSupportSettings(draft);
      setSettings(updated);
      setDraft(updated);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h3 className="card__title">Assinatura dos atendentes</h3>
      <p className="card__subtitle">
        O cliente vê um número só. A assinatura diz com quem ele está falando — vale para as mensagens
        escritas por pessoas; as do assistente continuam sem assinatura.
      </p>

      {error && <div className="alert alert--error">{error}</div>}
      {saved && !dirty && <div className="alert alert--success">Assinatura salva.</div>}

      <div className="checkbox">
        <input
          id="signature-enabled"
          type="checkbox"
          checked={draft.signatureEnabled}
          disabled={!isAdmin}
          onChange={(event) => update('signatureEnabled', event.target.checked)}
        />
        <label htmlFor="signature-enabled">
          Assinar as mensagens dos atendentes
          <div className="field__hint">Desligado, a mensagem sai exatamente como foi escrita.</div>
        </label>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="signature-department">Setor</label>
          <input
            id="signature-department"
            value={draft.signatureDepartment}
            disabled={!isAdmin || !draft.signatureEnabled}
            onChange={(event) => update('signatureDepartment', event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="signature-template">Modelo</label>
          <input
            id="signature-template"
            value={draft.signatureTemplate}
            disabled={!isAdmin || !draft.signatureEnabled}
            onChange={(event) => update('signatureTemplate', event.target.value)}
          />
          <div className="field__hint">
            <code>{'{nome}'}</code> vira o nome de quem está logado e <code>{'{setor}'}</code>, o campo ao lado.
          </div>
        </div>
      </div>

      {/* A prévia mostra a mensagem como ela chega ao WhatsApp, com o negrito
          já aplicado — é o único jeito de conferir o modelo sem mandar teste
          para um cliente de verdade. */}
      <div className="field">
        <label>Como o cliente recebe</label>
        <div className="signature-preview">
          {draft.signatureEnabled && preview && <div>{renderWhatsappBold(preview)}:</div>}
          <div>Bom dia! Já reiniciei o serviço aqui, pode testar novamente.</div>
        </div>
        {draft.signatureEnabled && !preview && (
          <div className="field__hint">
            O modelo não produz texto com estes valores — a mensagem sairia sem assinatura.
          </div>
        )}
      </div>

      <div className="btn-row">
        <button className="btn btn--primary" disabled={!isAdmin || saving || !dirty} onClick={() => void save()}>
          {saving ? 'Salvando' : 'Salvar assinatura'}
        </button>
        {dirty && (
          <button className="btn" onClick={() => setDraft(settings)}>
            Descartar alterações
          </button>
        )}
      </div>
    </div>
  );
}
